import {
  Autorenew,
  Clear,
  FileDownloadOutlined,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Search,
  Sort,
} from '@mui/icons-material'
import { experimentsList, getExperimentFiltersOptions } from '../../api/api'

import {
  Backdrop,
  Badge,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { get, isEmpty, isNil, map, size, toUpper } from 'lodash'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { ExperimentFiltersOptions, ExperimentListItem } from '../../api'
import BlueDot from '../../assets/blue_dot.svg'
import EmptyBox from '../../assets/empty_box.svg'
import FetchError from '../../assets/fetch_error.svg'
import RedDot from '../../assets/red_dot.svg'
import SortError from '../../assets/sort_error.svg'
import YellowDot from '../../assets/yellow_dot.svg'
import { ExperimentsTable } from '../../components/ExperimentsTable'
import CustomDialog from '../../components/dialogs/CustomDialog/CustomDialog'
import { FormFieldLabel } from '../../components/form/FormFieldLabel'
import { YEAR_BOUNDARY } from '../../constants/experimentYearBoundary'
import { AppStateContext } from '../../state/AppProvider'
import styles from './Experiments.module.css'
import FiltersDialog from './FiltersDialog/FiltersDialog'

const PAGE_SIZE = 15

const Experiments = () => {
  const appStateContext = useContext(AppStateContext)
  const [isBackdropOpen, setIsBackdropOpen] = useState(false)
  const [isExportCsvBackdropOpen, setIsExportCsvBackdropOpen] = useState(false)

  const [items, setItems] = useState<ExperimentListItem[] | null>(null)
  const [totalItems, setTotalItems] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const filters = appStateContext?.state.experimentListState.filters ?? { 'planning_basics.year': '2025' }
  const sortBy = appStateContext?.state.experimentListState.sortBy ?? 'Code'
  const sortOrder = appStateContext?.state.experimentListState.sortOrder ?? 'desc'

  const [isFiltersDialogOpen, setIsFiltersDialogOpen] = useState<boolean>(false)
  const [hasFetchError, setHasFetchError] = useState<boolean>(false)
  const [hasSortError, setHasSortError] = useState<boolean>(false)
  const [filterOptions, setFilterOptions] = useState<ExperimentFiltersOptions | null>(null)
  const [isDownloadCsvDialogOpen, setIsDownloadCsvDialogOpen] = useState<boolean>(false)
  const [search, setSearch] = useState<string>(appStateContext?.state.experimentListState.searchQuery ?? '')
  const [debouncedSearch] = useDebounce(search, 1000)

  const getSortBy = (property: string) => {
    switch (property) {
      case 'Code':
        return 'planning_basics.experiment_code'
      case 'Country':
        return 'planning_basics.country'
      case 'GLA Strategic Priority':
        return Number(get(filters, 'planning_basics.year')) > YEAR_BOUNDARY
          ? 'planning_basics.mto_initiative'
          : 'planning_basics.application_area'
      case 'Charter':
        return 'planning_basics.global_charter'
      case 'Methodology':
        return 'planning_basics.methodology'
      case 'Start Date':
        return 'setup_details.experiment_start_date'
      case 'Phase':
        return 'planning_basics.experiment_phase_high_level'
      case 'Brand':
        return 'planning_basics.brand'
      case 'High Value?':
        return 'extra_data.high_value'
      default:
        return 'planning_basics.experiment_code'
    }
  }

  const getHeadTitles = (year: string) => {
    if (year && Number(year) > YEAR_BOUNDARY) {
      return ['Code', 'Country', 'GLA Strategic Priority', 'Charter', 'Methodology', 'Start Date', 'Phase', '']
    }
    return ['Code', 'Country', 'GLA Strategic Priority', 'Methodology', 'Start Date', 'Phase', 'Brand', 'High Value?', '']
  }

  const getLastPage = useCallback(() => {
    return Math.ceil(totalItems / PAGE_SIZE)
  }, [totalItems])

  const getExperimentsCount = useMemo(() => {
    return {
      start: page * PAGE_SIZE - PAGE_SIZE + 1,
      end: Math.min(page * PAGE_SIZE, totalItems),
      total: totalItems,
    }
  }, [page, totalItems])

  const getExperiments = async ({
    currentPage,
    currentSortBy,
    currentSortOrder,
    filtersFromProps,
    isSorting = false,
  }: {
    currentPage: number
    currentSortBy: string
    currentSortOrder: 'asc' | 'desc'
    filtersFromProps: object
    isSorting?: boolean
  }) => {
    setIsBackdropOpen(true)

    await experimentsList({
      filters: JSON.stringify(filtersFromProps),
      search: debouncedSearch,
      projection: JSON.stringify({
        created_at: 1,
        'data.planning_basics.experiment_code': 1,
        'data.planning_basics.country': 1,
        'data.planning_basics.mto_initiative': 1,
        'data.planning_basics.application_area': 1,
        'data.planning_basics.global_charter': 1,
        'data.planning_basics.methodology': 1,
        'data.planning_basics.experiment_phase_high_level': 1,
        'data.setup_details.experiment_start_date': 1,
        'data.planning_basics.experiment_status.field_value': 1,
        'data.extra_data.folder_path.field_value': 1,
        'data.planning_basics.brand': 1,
        'data.extra_data.high_value': 1,
      }),
      sortBy: getSortBy(currentSortBy),
      sortOrder: currentSortOrder,
      page: currentPage,
      pageSize: PAGE_SIZE,
    }).then(data => {
      if (!data) {
        if (isSorting) {
          setHasSortError(true)
        } else {
          setHasFetchError(true)
        }
        setIsBackdropOpen(false)
        return
      }

      setItems(data.items || [])
      setTotalItems(data?.total || 0)
      setPage(currentPage)
      appStateContext?.dispatch({
        type: 'SET_EXPERIMENT_LIST_STATE',
        payload: {
          filters: filtersFromProps,
          sortBy: currentSortBy,
          sortOrder: currentSortOrder,
          searchQuery: debouncedSearch,
        },
      })
      setHasFetchError(false)
      setHasSortError(false)
    })
    setIsBackdropOpen(false)
  }

  useEffect(() => {
    if (isEmpty(items) && page > 1) {
      getExperiments({
        currentPage: page - 1,
        currentSortBy: sortBy,
        currentSortOrder: sortOrder,
        filtersFromProps: filters,
      })
    }
  }, [items])

  useEffect(() => {
    getExperiments({
      currentPage: page,
      currentSortBy: sortBy,
      currentSortOrder: sortOrder,
      filtersFromProps: filters,
    })
    getExperimentFiltersOptions().then(options => {
      setFilterOptions(options)
    })
  }, [])

  const handleSort = async (property: string) => {
    try {
      await getExperiments({
        currentPage: page,
        currentSortBy: property,
        currentSortOrder: sortOrder === 'asc' ? 'desc' : 'asc',
        filtersFromProps: filters,
        isSorting: true,
      })
    } catch (error) {
      setHasSortError(true)
    }
  }

  const getActiveFiltersCount = () => {
    return Object.values(filters || {}).filter(v => v !== undefined && v !== '' && v !== null).length - 1
  }

  const formatExperimentsToObject = (experiments: ExperimentListItem[]) => {
    return map(experiments, experiment => {
      return Number(get(filters, 'planning_basics.year')) > YEAR_BOUNDARY
        ? {
            'E@S Experiment': experiment.data.planning_basics.is_e_at_s_experiment?.field_value ?? '',
            Year: experiment.data.planning_basics.year?.field_value ?? '',
            Quarter: experiment.data.planning_basics.quarter?.field_value ?? '',
            OU: experiment.data.planning_basics.ou?.field_value ?? '',
            Country: experiment.data.planning_basics.country?.field_value ?? '',
            'Experiment Code': experiment.data.planning_basics.experiment_code?.field_value ?? '',
            'Experiment Phase: High Level':
              experiment.data.planning_basics.experiment_phase_high_level?.field_value ?? '',
            'DME and Resources Committed':
              experiment.data.planning_basics.dme_and_resources_committed?.field_value ?? '',
            'Action Statement': experiment.data.planning_basics.action_statement?.field_value ?? '',
            'Global Category Name': experiment.data.planning_basics.global_category_name?.field_value ?? '',
            Brand: experiment.data.planning_basics.brand?.field_value ?? '',
            Bottler: experiment.data.planning_basics.bottler?.field_value ?? '',
            'GLA Strategic Priority': experiment.data.planning_basics.mto_initiative?.field_value ?? '',
            'Key Question': experiment.data.planning_basics.key_question?.field_value ?? '',
            'Hypothesis Templates': experiment.data.planning_basics.hypothesis_template?.field_value ?? '',
            Hypothesis: experiment.data.planning_basics.hypothesis?.field_value ?? '',
            'Global Charter': experiment.data.planning_basics.global_charter?.field_value ?? '',
            'Local Charter': experiment.data.planning_basics.local_charter?.field_value ?? '',
            Methodology: experiment.data.planning_basics.methodology?.field_value ?? '',
            'Leading KPI': experiment.data.planning_basics.leading_kpi?.field_value ?? '',
            'Experiment Start Date': experiment.data.setup_details.experiment_start_date?.field_value ?? '',
            'Experiment End Date': experiment.data.setup_details.experiment_end_date?.field_value ?? '',
            'Final Result Submission Date':
              experiment.data.setup_details.final_result_submission_date?.field_value ?? '',
            'Experiment Budget (USD)': experiment.data.setup_details.experiment_budget?.field_value ?? '',
            'Analytical Team': experiment.data.setup_details.analytical_team?.field_value ?? '',
            'Campaign Name': experiment.data.setup_details.campaign_name?.field_value ?? '',
            Platform: experiment.data.setup_details.platform_partner?.field_value ?? '',
            'Hypothesis Proven?': experiment.data.completion.hypothesis_proven?.field_value ?? '',
            'Confidence In Results': experiment.data.completion.confidence_in_results?.field_value ?? '',
            'Total Experiment DME Spend (USD)':
              experiment.data.completion.total_experiment_dme_spend?.field_value ?? '',
            'Additional Comments': experiment.data.completion.additional_comments?.field_value ?? '',
            'Link to the Experiment Charter & Outcomes': experiment.data.extra_data.folder_path?.field_value ?? '',
            'Updated As Of': experiment.updated_at ?? '',
            'Updated By': experiment.data.planning_basics.updated_by_name?.field_value ?? '',
            'Submission Status': experiment.data.planning_basics.experiment_status?.field_value ?? '',
            'Readiness Score': experiment.data.extra_data.readiness_score_for_chart?.field_value ?? '',
            'Value Score': experiment.data.extra_data.value_score_for_chart?.field_value ?? '',
            'Segmentation Category': experiment.data.extra_data.segmentation_category?.field_value ?? '',
            'Created At': experiment.created_at ?? '',
          }
        : {
            'E@S Experiment': experiment.data.planning_basics.is_e_at_s_experiment?.field_value ?? '',
            OU: experiment.data.planning_basics.ou?.field_value ?? '',
            Country: experiment.data.planning_basics.country?.field_value ?? '',
            'DME and Resources Committed':
              experiment.data.planning_basics.dme_and_resources_committed?.field_value ?? '',
            Methodology: experiment.data.planning_basics.methodology?.field_value ?? '',
            'Global Category Name': experiment.data.planning_basics.global_category_name?.field_value ?? '',
            Brand: experiment.data.planning_basics.brand?.field_value ?? '',
            'GLA Strategic Priority': experiment.data.planning_basics.application_area?.field_value ?? '',
            'E@S GLA Question': experiment.data.planning_basics.gla_question?.field_value ?? '',
            'Experiment Code': experiment.data.planning_basics.experiment_code?.field_value ?? '',
            'Experiment Phase: High Level':
              experiment.data.planning_basics.experiment_phase_high_level?.field_value ?? '',
            Year: experiment.data.planning_basics.year?.field_value ?? '',
            Quarter: experiment.data.planning_basics.quarter?.field_value ?? '',
            'Action Statement': experiment.data.planning_basics.action_statement?.field_value ?? '',
            'Experiment Start Date': experiment.data.setup_details.experiment_start_date?.field_value ?? '',
            'Experiment End Date': experiment.data.setup_details.experiment_end_date?.field_value ?? '',
            'Experiment Budget (USD)': experiment.data.setup_details.experiment_budget?.field_value ?? '',
            'Leading KPI': experiment.data.planning_basics.leading_kpi?.field_value ?? '',
            'Hypothesis Templates': experiment.data.planning_basics.hypothesis_template?.field_value ?? '',
            Hypothesis: experiment.data.planning_basics.hypothesis?.field_value ?? '',
            'E@S Learning Code': experiment.data.setup_details.learning_code?.field_value ?? '',
            'Analytical Team': experiment.data.setup_details.analytical_team?.field_value ?? '',
            Platform: experiment.data.setup_details.platform_partner?.field_value ?? '',
            'Hypothesis Proven?': experiment.data.completion.hypothesis_proven?.field_value ?? '',
            'Confidence In Results': experiment.data.completion.confidence_in_results?.field_value ?? '',
            'Total Experiment DME Spend (USD)':
              experiment.data.completion.total_experiment_dme_spend?.field_value ?? '',
            'Link to the Experiment Charter & Outcomes': experiment.data.extra_data.folder_path?.field_value ?? '',
            'Additional Comments': experiment.data.completion.additional_comments?.field_value ?? '',
            'Orchestrator Review': isNil(experiment.data.extra_data.approval?.field_value)
              ? ''
              : toUpper(String(experiment.data.extra_data.approval?.field_value)),
            'Updated As Of': experiment.updated_at ?? '',
            'Updated By': experiment.data.planning_basics.updated_by_name?.field_value ?? '',
            'Experiment Status': experiment.data.planning_basics.experiment_status?.field_value ?? '',
            CampaignName: experiment.data.setup_details.campaign_name?.field_value ?? '',
            'HighValue?': experiment.data.extra_data.high_value?.field_value ?? '',
          }
    })
  }

  const convertToCSV = (data: object[], headers: string[]) => {
    const escapeCSVValue = (value: string | null | undefined): string => {
      if (value === null || value === undefined) {
        return ''
      }
      const stringValue = String(value)

      if (
        ['=', '+', '-', '@'].includes(stringValue[0]) ||
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n') ||
        stringValue.includes('\r')
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    }

    const headerRow = headers.map((h: string) => escapeCSVValue(h)).join(',')
    const dataRows = data.map((row: any) => headers.map((h: string) => escapeCSVValue(row[h])).join(',')).join('\n')

    return `${headerRow}\n${dataRows}`
  }

  const handleExportCsv = async () => {
    setIsDownloadCsvDialogOpen(false)
    setIsExportCsvBackdropOpen(true)

    try {
      const experiments: object[] = []
      const totalPages = getLastPage()
      const batchSize = 5

      for (let batchStart = 1; batchStart <= totalPages; batchStart += batchSize) {
        const batchEnd = Math.min(batchStart + batchSize - 1, totalPages)
        const batchPromises = []

        for (let i = batchStart; i <= batchEnd; i++) {
          batchPromises.push(
            experimentsList({
              filters: JSON.stringify(filters),
              search: debouncedSearch,
              sortBy: getSortBy(sortBy),
              sortOrder: sortOrder,
              page: i,
              pageSize: PAGE_SIZE,
            }),
          )
        }

        const batchResults = await Promise.all(batchPromises)

        batchResults.forEach(data => {
          if (data && data.items) {
            experiments.push(...formatExperimentsToObject(data.items))
          }
        })

        if (batchEnd < totalPages) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      if (isEmpty(experiments)) {
        return
      }
      const csvData = new Blob([convertToCSV(experiments, Object.keys(experiments[0]))], { type: 'text/csv' })
      const csvURL = URL.createObjectURL(csvData)
      const link = document.createElement('a')
      link.href = csvURL

      const now = new Date()
      const formattedDate = now.toISOString().replace(/:/g, '_')

      link.download = `Export_${formattedDate}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(csvURL)
    } catch (error) {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Download failed. Try again later.', severity: 'warning' },
      })
    } finally {
      setIsExportCsvBackdropOpen(false)
    }
  }
  const getBackdropMessage = () => {
    if (isExportCsvBackdropOpen) {
      return 'Exporting CSV...'
    } else if (isBackdropOpen) {
      return 'Fetching Experiments'
    }
    return ''
  }

  useEffect(() => {
    if (size(debouncedSearch) >= 2 || isEmpty(debouncedSearch)) {
      getExperiments({
        currentPage: 1,
        currentSortBy: 'Code',
        currentSortOrder: 'desc',
        filtersFromProps: filters,
      })
    }
  }, [debouncedSearch])

  const shouldResetFiltersOnYearChange = (currentYear: string | undefined, newYear: string): boolean => {
    const currentYearNum = Number(currentYear)
    const newYearNum = Number(newYear)

    const isCrossingBoundary =
      (currentYearNum > YEAR_BOUNDARY && newYearNum <= YEAR_BOUNDARY) ||
      (currentYearNum <= YEAR_BOUNDARY && newYearNum > YEAR_BOUNDARY)

    return isCrossingBoundary
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <p className={styles.headerText}>All Experiments</p>
        </div>
        <div className={styles.content}>
          <div className={styles.filters}>
            <div className={styles.yearFilter}>
              <FormFieldLabel
                text="Year of Experiment"
                isAiGenerated={false}
              />
              <FormControl fullWidth>
                <Select
                  size="small"
                  value={get(filters, 'planning_basics.year') ?? '2025'}
                  onChange={(e: SelectChangeEvent) => {
                    getExperiments({
                      currentPage: 1,
                      currentSortBy: 'Code',
                      currentSortOrder: 'desc',
                      filtersFromProps: shouldResetFiltersOnYearChange(
                        get(filters, 'planning_basics.year'),
                        e.target.value,
                      )
                        ? { 'planning_basics.year': e.target.value }
                        : { ...filters, 'planning_basics.year': e.target.value },
                    })
                  }}>
                  {map(filterOptions?.year, year => (
                    <MenuItem
                      key={year}
                      value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className={styles.searchContainer}>
              <FormControl fullWidth>
                <TextField
                  size="small"
                  variant="outlined"
                  classes={{
                    root: styles.searchInput,
                  }}
                  slotProps={{
                    input: {
                      startAdornment: <Search className={styles.searchIcon} />,
                      endAdornment: !isEmpty(search) ? (
                        <Clear
                          className={styles.clearIcon}
                          onClick={() => setSearch('')}
                        />
                      ) : null,
                    },
                  }}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search experiments..."
                />
              </FormControl>
            </div>
            <Badge
              badgeContent={getActiveFiltersCount()}
              invisible={getActiveFiltersCount() === 0}
              classes={{ badge: styles.filtersButtonBadge }}>
              <Button
                variant="contained"
                endIcon={<Sort />}
                className={styles.filtersButton}
                onClick={() => setIsFiltersDialogOpen(true)}>
                <p className={styles.buttonText}>Filters</p>
              </Button>
            </Badge>
            <div className={styles.legendContainer}>
              <div className={styles.legendItem}>
                <img src={YellowDot} />
                <p className={styles.legendItemText}>Draft</p>
              </div>
              <div className={styles.legendItem}>
                <img src={BlueDot} />
                <p className={styles.legendItemText}>Submitted</p>
              </div>
              <div className={styles.legendItem}>
                <img src={RedDot} />
                <p className={styles.legendItemText}>Pending Resubmission</p>
              </div>
            </div>
            <div className={styles.headerButtonsContainer}>
              <Button
                disabled={totalItems === 0}
                variant="contained"
                startIcon={<FileDownloadOutlined />}
                className={styles.exportCsvButton}
                onClick={() => {
                  setIsDownloadCsvDialogOpen(true)
                }}>
                <p className={styles.exportCsvButtonText}>Export CSV</p>
              </Button>
              <Button
                variant="contained"
                startIcon={<Autorenew />}
                className={styles.refreshButton}
                onClick={() => {
                  getExperiments({
                    currentPage: 1,
                    currentSortBy: 'Code',
                    currentSortOrder: 'desc',
                    filtersFromProps: filters,
                  })
                }}>
                <p className={styles.refreshButtonText}>Refresh</p>
              </Button>
            </div>
          </div>
          {hasSortError || hasFetchError || isEmpty(items) ? (
            <div className={styles.noExperimentsContainer}>
              {hasSortError ? (
                <img src={SortError} />
              ) : hasFetchError ? (
                <img src={FetchError} />
              ) : (
                <img src={EmptyBox} />
              )}
              <p className={styles.noExperimentsHeader}>
                {hasSortError
                  ? 'Error Sorting List'
                  : hasFetchError
                    ? 'Error Fetching Experiments'
                    : 'No record to display at the moment'}
              </p>
              <p className={styles.noExperimentsText}>
                {hasSortError
                  ? 'An issue occurred while sorting the list. Please refresh or try again in a few minutes'
                  : hasFetchError
                    ? 'There was an issue retrieving the experiments. Check your connection and try again. If the error persists, contact the support team.'
                    : 'Dive in, unleash your potential, and fill this space with your experiments!'}
              </p>
            </div>
          ) : (
            <div className={styles.experimentsContainer}>
              <ExperimentsTable
                headTitles={getHeadTitles(get(filters, 'planning_basics.year'))}
                order={sortOrder}
                orderBy={sortBy}
                year={get(filters, 'planning_basics.year') ? Number(get(filters, 'planning_basics.year')) : undefined}
                data={items ?? []}
                onRequestSort={handleSort}
                refreshData={() => {
                  getExperiments({
                    currentPage: page,
                    currentSortBy: sortBy,
                    currentSortOrder: sortOrder,
                    filtersFromProps: filters,
                  })
                }}
              />
              <div className={styles.footerContainer}>
                <p className={styles.experimentsCountText}>
                  Showing {getExperimentsCount.start} to {getExperimentsCount.end} of {getExperimentsCount.total} items
                </p>

                <div className={styles.paginationContainer}>
                  <IconButton
                    className={styles.paginationButton}
                    disabled={page === 1}
                    onClick={() => {
                      getExperiments({
                        currentPage: 1,
                        currentSortBy: sortBy,
                        currentSortOrder: sortOrder,
                        filtersFromProps: filters,
                      })
                    }}>
                    <KeyboardDoubleArrowLeft />
                  </IconButton>
                  <IconButton
                    className={styles.paginationButton}
                    disabled={page === 1}
                    onClick={() => {
                      getExperiments({
                        currentPage: page - 1,
                        currentSortBy: sortBy,
                        currentSortOrder: sortOrder,
                        filtersFromProps: filters,
                      })
                    }}>
                    <KeyboardArrowLeft />
                  </IconButton>
                  <p>{`${page} - ${getLastPage()}`}</p>
                  <IconButton
                    className={styles.paginationButton}
                    disabled={page === getLastPage()}
                    onClick={() => {
                      getExperiments({
                        currentPage: page + 1,
                        currentSortBy: sortBy,
                        currentSortOrder: sortOrder,
                        filtersFromProps: filters,
                      })
                    }}>
                    <KeyboardArrowRight />
                  </IconButton>
                  <IconButton
                    className={styles.paginationButton}
                    disabled={page === getLastPage()}
                    onClick={() => {
                      getExperiments({
                        currentPage: getLastPage(),
                        currentSortBy: sortBy,
                        currentSortOrder: sortOrder,
                        filtersFromProps: filters,
                      })
                    }}>
                    <KeyboardDoubleArrowRight />
                  </IconButton>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <FiltersDialog
        open={isFiltersDialogOpen}
        primaryAction={async (filters: object) => {
          setIsFiltersDialogOpen(false)
          await getExperiments({
            currentPage: 1,
            currentSortBy: 'Code',
            currentSortOrder: 'desc',
            filtersFromProps: filters,
          })
        }}
        primaryActionButtonText="Apply Filters"
        title="Filters"
        filters={filters}
        filterOptions={filterOptions}
        secondaryAction={() => {
          setIsFiltersDialogOpen(false)
        }}
        secondaryActionButtonText="Cancel"
        year={get(filters, 'planning_basics.year') ? Number(get(filters, 'planning_basics.year')) : undefined}
      />
      <Backdrop
        className={styles.backdrop}
        sx={theme => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={isBackdropOpen || isExportCsvBackdropOpen}>
        <div className={styles.backdropMessageContainer}>
          <CircularProgress color="inherit" />
          <p className={styles.backdropMessage}>{getBackdropMessage()}</p>
        </div>
      </Backdrop>
      <CustomDialog
        title="Export CSV"
        open={isDownloadCsvDialogOpen}
        setClose={() => {
          setIsDownloadCsvDialogOpen(false)
        }}
        primaryAction={() => {
          handleExportCsv()
        }}
        secondaryAction={() => {
          setIsDownloadCsvDialogOpen(false)
        }}
        primaryActionButtonText="Export"
        secondaryActionButtonText="Cancel">
        <Typography className={styles.exportCsvDialogContentText}>
          Are you sure that you want to export the current list of <strong>{totalItems}</strong> items?
        </Typography>
      </CustomDialog>
    </>
  )
}

export default Experiments
